import React, { Fragment } from 'react';
import { get } from 'lodash-es';
import { Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { validateErrorCode } from './utils';

const Step3TableColumns = (
  handleModal,
  editLot,
  handleCheckbox,
  handleCheckboxAll,
  selectedLots,
  spreadsheetErrors,
  type,
  isGoldPlatinum,
) => {
  // curryng for reducer params quantity and code readeable
  const validateError = (lotInvId, lotNumberId, rowInfo, errorCode) => validateErrorCode(isGoldPlatinum, lotInvId, lotNumberId, rowInfo, spreadsheetErrors, errorCode);
  const validateMultipleError = (lotInvId, lotNumberId, rowInfo, errorCodes) => errorCodes.reduce((acc, v) => validateError(lotInvId, lotNumberId, rowInfo, v) || acc, false);
  // The following items will be in both live and timed sales
  const tableColumns = [
    {
      Header: () => (
        <Fragment>
          {selectedLots.size > 0 ? (
            <FontAwesomeIcon
              className="color-blue cursor-pointer"
              icon="minus-square"
              onClick={() => handleCheckboxAll()}
            />
          ) : (
            <Input
              className="ml-0 position-relative"
              checked={false}
              onChange={() => handleCheckboxAll()}
              type="checkbox"
            />
          )}
        </Fragment>
      ),
      headerClassName: 'd-flex justify-content-center align-items-center',
      id: 'selectIcon',
      fixed: 'left',
      width: 55,
      sortable: false,
      accessor: d => ({
        id: d.id
      }),
      Cell: ({ value: { id } }) => (
        <div className="text-center">
          <Input
            className="ml-0 position-relative"
            checked={selectedLots.has(id)}
            onChange={() => handleCheckbox(id)}
            type="checkbox"
          />
        </div>
      )
    },
    {
      Header: 'Actions',
      id: 'actions',
      fixed: 'left',
      width: 70,
      sortable: false,
      accessor: d => ({
        id: d.id
      }),
      Cell: ({ value: { id } }) => (
        <div className="px-2 text-center">
          <FontAwesomeIcon
            className="color-blue cursor-pointer"
            icon="edit"
            onClick={() => editLot(id)}
          />
          <FontAwesomeIcon
            className="color-blue cursor-pointer ml-2"
            icon="trash-alt"
            onClick={() => handleModal('deleteLotModal', true, null, id)}
          />
        </div>
      )
    },
    {
      Header: 'Lot #*',
      id: 'lotNumber',
      width: 70,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }
        const lotNum = get(rowInfo, 'original.lotNumber');
        const lotId = lotNum + get(rowInfo, 'original.lotExt');

        const lotError = (isGoldPlatinum && !lotNum) ? false
          : (get(spreadsheetErrors, '[108].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[109].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[110].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[111].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[112].lotNumbers', new Set()).has(lotId));

        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        lotNumber: d.lotNumber
      }),
      Cell: ({ value: { lotNumber } }) => <p>{lotNumber}</p>
    },
    {
      Header: 'Lot Ext.',
      id: 'lotExt',
      width: 80,
      sortable: false,
      accessor: d => ({
        lotExt: d.lotExt
      }),
      Cell: ({ value: { lotExt } }) => <p>{lotExt}</p>
    },
    {
      Header: 'Inventory #',
      id: 'inventoryNumber',
      width: 100,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotNumberId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotInvId = get(rowInfo, 'original.invNumber');

        return {
          className: validateMultipleError(lotInvId, lotNumberId, rowInfo, [120, 121]) ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        invNumber: d.invNumber
      }),
      Cell: ({ value: { invNumber } }) => <p>{invNumber}</p>
    },
    {
      Header: 'Title*',
      id: 'title',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotNumberId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotInvId = get(rowInfo, 'original.invNumber');

        return {
          className: validateMultipleError(lotInvId, lotNumberId, rowInfo, [101, 165]) ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        lotTitle: d.lotTitle
      }),
      Cell: ({ value: { lotTitle } }) => (
        <p>{lotTitle.length > 50 ? `${lotTitle.substring(0, 50)}...` : lotTitle}</p>
      )
    },
    {
      Header: 'Description*',
      id: 'description',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const invNumberId = get(rowInfo, 'original.invNumber');

        return {
          className: (validateError(invNumberId, lotId, rowInfo, 164)) ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        lotDescription: d.lotDescription
      }),
      Cell: ({ value: { lotDescription } }) => (
        <p>
          {lotDescription.length > 50 ? `${lotDescription.substring(0, 50)}...` : lotDescription}
        </p>
      )
    },
    {
      Header: 'Lo Est.',
      id: 'lowEstimate',
      width: 65,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[122].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[127].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[142-loEst].lotNumbers', new Set()).has(lotId);

        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        loEst: d.loEst
      }),
      Cell: ({ value: { loEst } }) => <p>{loEst}</p>
    },
    {
      Header: 'Hi Est.',
      id: 'highEstimate',
      width: 65,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const invNumberId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');

        return {
          className: validateMultipleError(invNumberId, lotId, rowInfo, [123, '142-hiEst']) ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        hiEst: d.hiEst
      }),
      Cell: ({ value: { hiEst } }) => <p>{hiEst}</p>
    },
    {
      Header: 'Starting Bid',
      id: 'startingBid',
      width: 100,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[124].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[142-startingBid].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        startingBid: d.startingBid
      }),
      Cell: ({ value: { startingBid } }) => <p>{startingBid}</p>
    },
    {
      Header: 'Item Dimensions',
      id: 'dimensions',
      width: 140,
      accessor: d => ({
        dimensions: d.dimensions
      }),
      Cell: ({ value: { dimensions } }) => <p>{dimensions}</p>
    },
    {
      Header: 'Shipping Length',
      id: 'shipLength',
      width: 140,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const invNumberId = get(rowInfo, 'original.invNumber');
        const lotError = get(spreadsheetErrors, '[172].lotNumbers', new Set()).has(lotId);
        const invError = get(spreadsheetErrors, '[172].invNumbers', new Set()).has(invNumberId);
        return {
          className: (lotError || invError) ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        shipLength: d.shipLength
      }),
      Cell: ({ value: { shipLength } }) => <p>{shipLength}</p>
    },
    {
      Header: 'Shipping Height',
      id: 'shipHeight',
      width: 140,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const invNumberId = get(rowInfo, 'original.invNumber');
        const lotError = get(spreadsheetErrors, '[170].lotNumbers', new Set()).has(lotId);
        const invError = get(spreadsheetErrors, '[170].invNumbers', new Set()).has(invNumberId);
        return {
          className: (lotError || invError) ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        shipHeight: d.shipHeight
      }),
      Cell: ({ value: { shipHeight } }) => <p>{shipHeight}</p>
    },
    {
      Header: 'Shipping Width',
      id: 'shipWidth',
      width: 140,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const invNumberId = get(rowInfo, 'original.invNumber');
        const lotError = get(spreadsheetErrors, '[173].lotNumbers', new Set()).has(lotId);
        const invError = get(spreadsheetErrors, '[173].invNumbers', new Set()).has(invNumberId);
        return {
          className: (lotError || invError) ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        shipWidth: d.shipWidth
      }),
      Cell: ({ value: { shipWidth } }) => <p>{shipWidth}</p>
    },
    {
      Header: 'Total Weight',
      id: 'shipWeight',
      width: 140,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const invNumberId = get(rowInfo, 'original.invNumber');
        const lotError = get(spreadsheetErrors, '[171].lotNumbers', new Set()).has(lotId);
        const invError = get(spreadsheetErrors, '[171].invNumbers', new Set()).has(invNumberId);
        return {
          className: (lotError || invError) ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        shipWeight: d.shipWeight
      }),
      Cell: ({ value: { shipWeight } }) => <p>{shipWeight}</p>
    },
    {
      Header: 'Condition',
      id: 'condition',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      accessor: d => ({
        condition: d.condition
      }),
      Cell: ({ value: { condition } }) => (
        <p>{condition.length > 50 ? `${condition.substring(0, 50)}...` : condition}</p>
      )
    },
    {
      Header: 'Other Notes',
      id: 'notes',
      width: 260,
      accessor: d => ({
        notes: d.notes
      }),
      Cell: ({ value: { notes } }) => (
        <p>{notes.length > 50 ? `${notes.substring(0, 50)}...` : notes}</p>
      )
    },
    {
      Header: 'Provenance',
      id: 'provenance',
      width: 100,
      accessor: d => ({
        provenance: d.provenance
      }),
      Cell: ({ value: { provenance } }) => <p>{provenance}</p>
    },
    {
      Header: 'Artist Name',
      id: 'artistName',
      width: 100,
      accessor: d => ({
        artistName: d.artistName
      }),
      Cell: ({ value: { artistName } }) => <p>{artistName}</p>
    },
    {
      Header: 'Exhibited',
      id: 'exhibited',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      accessor: d => ({
        exhibited: d.exhibited
      }),
      Cell: ({ value: { exhibited } }) => (
        <p>{exhibited.length > 50 ? `${exhibited.substring(0, 50)}...` : exhibited}</p>
      )
    },
    {
      Header: 'Literature',
      id: 'literature',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      accessor: d => ({
        literature: d.literature
      }),
      Cell: ({ value: { literature } }) => (
        <p>{literature.length > 50 ? `${literature.substring(0, 50)}...` : literature}</p>
      )
    },
    {
      Header: 'Medium',
      id: 'medium',
      width: 100,
      accessor: d => ({
        medium: d.medium
      }),
      Cell: ({ value: { medium } }) => <p>{medium}</p>
    },
    {
      Header: 'Circa',
      id: 'circa',
      width: 100,
      accessor: d => ({
        circa: d.circa
      }),
      Cell: ({ value: { circa } }) => <p>{circa}</p>
    }
  ];

  if (type.toLowerCase() === 'timed') {
    // Insert this timed only column at a specific location in the columns
    tableColumns.splice(13, 0, {
      Header: 'Reserve',
      id: 'reserve',
      width: 100,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[126].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[142-reserve].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        reserve: d.reserve
      }),
      Cell: ({ value: { reserve } }) => <p>{reserve}</p>
    });
  }

  return tableColumns;
};

export default Step3TableColumns;
